import React, { useState, useEffect } from 'react';
import { api } from '../../lib/api';
import { useSmsFeature } from '../../contexts/FeatureFlagContext';

interface StripeSettings {
  secretKey: string;
  publishableKey: string;
  webhookSecret: string;
}

interface EmailSettings {
  postmarkToken: string;
  fromEmail: string;
  fromName: string;
}

interface GooglePlacesSettings {
  apiKey: string;
}

interface TwilioSettings {
  accountSid: string;
  authToken: string;
  phoneNumber: string;
}

interface PricingSettings {
  plan_price_starter: string;
  plan_price_growth: string;
  plan_price_agency: string;
  pricing_email_per_send: string;
  pricing_sms_per_send: string;
  pricing_contact_per_month: string;
  pricing_feedback_per_item: string;
  included_emails_starter: string;
  included_emails_growth: string;
  included_emails_agency: string;
  included_sms_starter: string;
  included_sms_growth: string;
  included_sms_agency: string;
  included_contacts_starter: string;
  included_contacts_growth: string;
  included_contacts_agency: string;
}

interface PublicSetting {
  key: string;
  value: string;
  description?: string;
}

export const ServerSettingsPage: React.FC = () => {
  const isSmsEnabled = useSmsFeature();
  const [stripeSettings, setStripeSettings] = useState<StripeSettings>({
    secretKey: '',
    publishableKey: '',
    webhookSecret: ''
  });
  
  const [emailSettings, setEmailSettings] = useState<EmailSettings>({
    postmarkToken: '',
    fromEmail: 'noreply@repman.com',
    fromName: 'ReputationManager'
  });
  
  const [googlePlacesSettings, setGooglePlacesSettings] = useState<GooglePlacesSettings>({
    apiKey: ''
  });
  
  const [twilioSettings, setTwilioSettings] = useState<TwilioSettings>({
    accountSid: '',
    authToken: '',
    phoneNumber: ''
  });
  
  const [pricingSettings, setPricingSettings] = useState<PricingSettings>({
    plan_price_starter: '49',
    plan_price_growth: '149',
    plan_price_agency: '399',
    pricing_email_per_send: '0.002',
    pricing_sms_per_send: '0.015',
    pricing_contact_per_month: '0.01',
    pricing_feedback_per_item: '0.10',
    included_emails_starter: '1000',
    included_emails_growth: '5000',
    included_emails_agency: '20000',
    included_sms_starter: '100',
    included_sms_growth: '500',
    included_sms_agency: '2000',
    included_contacts_starter: '500',
    included_contacts_growth: '2500',
    included_contacts_agency: '10000'
  });

  const [publicSettings, setPublicSettings] = useState<PublicSetting[]>([]);
  const [newPublicSetting, setNewPublicSetting] = useState<PublicSetting>({
    key: '',
    value: '',
    description: ''
  });

  const [cardRequiredForTrial, setCardRequiredForTrial] = useState(false);
  
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [activeTab, setActiveTab] = useState<'stripe' | 'email' | 'twilio' | 'google-places' | 'pricing' | 'billing' | 'public'>('stripe');

  // Load settings on component mount
  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setIsLoading(true);
      
      // Load Stripe settings
      try {
        const stripeResponse = await api.serverAdmin.getStripeSettings();
        setStripeSettings(prev => ({
          ...prev,
          secretKey: stripeResponse.data.secret_key || '',
          publishableKey: stripeResponse.data.publishable_key || ''
        }));
      } catch (error) {
        console.warn('Failed to load Stripe settings:', error);
      }

      // Load email settings
      try {
        const emailResponse = await api.serverAdmin.getEmailSettings();
        setEmailSettings(prev => ({
          ...prev,
          postmarkToken: emailResponse.data.postmark_token || ''
        }));
      } catch (error) {
        console.warn('Failed to load email settings:', error);
      }

      // Load Google Places settings
      try {
        const googleResponse = await api.serverAdmin.getGooglePlacesSettings();
        setGooglePlacesSettings(prev => ({
          ...prev,
          apiKey: googleResponse.data.api_key || ''
        }));
      } catch (error) {
        console.warn('Failed to load Google Places settings:', error);
      }

      // Load Twilio settings
      try {
        const twilioResponse = await api.serverAdmin.getTwilioSettings();
        setTwilioSettings(prev => ({
          ...prev,
          accountSid: twilioResponse.data.account_sid || '',
          authToken: twilioResponse.data.auth_token || '',
          phoneNumber: twilioResponse.data.phone_number || ''
        }));
      } catch (error) {
        console.warn('Failed to load Twilio settings:', error);
      }

      // Load Pricing settings
      try {
        const pricingResponse = await api.serverAdmin.getPricingSettings();
        setPricingSettings(prev => ({
          ...prev,
          ...pricingResponse.data
        }));
      } catch (error) {
        console.warn('Failed to load pricing settings:', error);
      }

      // Load public settings
      try {
        const publicResponse = await api.serverAdmin.getPublicSettings();
        setPublicSettings(publicResponse.data || []);
      } catch (error) {
        console.warn('Failed to load public settings:', error);
      }

      // Load card required for trial setting
      try {
        const cardRequiredResponse = await api.serverAdmin.getCardRequiredForTrial();
        setCardRequiredForTrial(cardRequiredResponse.data.enabled || false);
      } catch (error) {
        console.warn('Failed to load card required for trial setting:', error);
        // Default to false if not set
        setCardRequiredForTrial(false);
      }

    } catch (error) {
      console.error('Failed to load settings:', error);
      setMessage({ type: 'error', text: 'Failed to load settings' });
    } finally {
      setIsLoading(false);
    }
  };

  const saveStripeSettings = async () => {
    try {
      setIsSaving(true);
      setMessage(null);

      await api.serverAdmin.updateStripeSettings({
        secret_key: stripeSettings.secretKey,
        publishable_key: stripeSettings.publishableKey
      });

      setMessage({ type: 'success', text: 'Stripe settings saved successfully' });
    } catch (error: any) {
      console.error('Failed to save Stripe settings:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Failed to save Stripe settings' 
      });
    } finally {
      setIsSaving(false);
    }
  };

  const saveEmailSettings = async () => {
    try {
      setIsSaving(true);
      setMessage(null);

      await api.serverAdmin.updateEmailSettings({
        postmark_token: emailSettings.postmarkToken
      });

      setMessage({ type: 'success', text: 'Email settings saved successfully' });
    } catch (error: any) {
      console.error('Failed to save email settings:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Failed to save email settings' 
      });
    } finally {
      setIsSaving(false);
    }
  };

  const saveGooglePlacesSettings = async () => {
    try {
      setIsSaving(true);
      setMessage(null);

      await api.serverAdmin.updateGooglePlacesSettings({
        api_key: googlePlacesSettings.apiKey
      });

      setMessage({ type: 'success', text: 'Google Places settings saved successfully' });
    } catch (error: any) {
      console.error('Failed to save Google Places settings:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Failed to save Google Places settings' 
      });
    } finally {
      setIsSaving(false);
    }
  };

  const saveTwilioSettings = async () => {
    try {
      setIsSaving(true);
      setMessage(null);

      await api.serverAdmin.updateTwilioSettings({
        account_sid: twilioSettings.accountSid,
        auth_token: twilioSettings.authToken,
        phone_number: twilioSettings.phoneNumber
      });

      setMessage({ type: 'success', text: 'Twilio settings saved successfully' });
    } catch (error: any) {
      console.error('Failed to save Twilio settings:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Failed to save Twilio settings' 
      });
    } finally {
      setIsSaving(false);
    }
  };

  const savePricingSettings = async () => {
    try {
      setIsSaving(true);
      setMessage(null);

      await api.serverAdmin.updatePricingSettings(pricingSettings);

      setMessage({ type: 'success', text: 'Pricing settings saved successfully' });
    } catch (error: any) {
      console.error('Failed to save pricing settings:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Failed to save pricing settings' 
      });
    } finally {
      setIsSaving(false);
    }
  };

  const saveCardRequiredForTrial = async () => {
    try {
      setIsSaving(true);
      setMessage(null);

      await api.serverAdmin.updateCardRequiredForTrial({
        enabled: cardRequiredForTrial
      });

      setMessage({ type: 'success', text: 'Card required for trial setting saved successfully' });
    } catch (error: any) {
      console.error('Failed to save card required for trial setting:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Failed to save card required for trial setting' 
      });
    } finally {
      setIsSaving(false);
    }
  };

  const addPublicSetting = async () => {
    if (!newPublicSetting.key || !newPublicSetting.value) {
      setMessage({ type: 'error', text: 'Key and value are required' });
      return;
    }

    try {
      setIsSaving(true);
      setMessage(null);

      await api.serverAdmin.setPublicSetting(newPublicSetting.key, {
        value: newPublicSetting.value,
        description: newPublicSetting.description
      });

      setNewPublicSetting({ key: '', value: '', description: '' });
      await loadSettings(); // Reload to get updated list
      setMessage({ type: 'success', text: 'Public setting added successfully' });
    } catch (error: any) {
      console.error('Failed to add public setting:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Failed to add public setting' 
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    if (name.startsWith('stripe.')) {
      const field = name.split('.')[1] as keyof StripeSettings;
      setStripeSettings(prev => ({ ...prev, [field]: value }));
    } else if (name.startsWith('email.')) {
      const field = name.split('.')[1] as keyof EmailSettings;
      setEmailSettings(prev => ({ ...prev, [field]: value }));
    } else if (name.startsWith('twilio.')) {
      const field = name.split('.')[1] as keyof TwilioSettings;
      setTwilioSettings(prev => ({ ...prev, [field]: value }));
    } else if (name.startsWith('google.')) {
      const field = name.split('.')[1] as keyof GooglePlacesSettings;
      setGooglePlacesSettings(prev => ({ ...prev, [field]: value }));
    } else if (name.startsWith('pricing_') || name.startsWith('included_') || name.startsWith('plan_price_')) {
      const field = name as keyof PricingSettings;
      setPricingSettings(prev => ({ ...prev, [field]: value }));
    } else if (name.startsWith('public.')) {
      const field = name.split('.')[1] as keyof PublicSetting;
      setNewPublicSetting(prev => ({ ...prev, [field]: value }));
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h1 className="text-2xl font-bold text-gray-900">Server Settings</h1>
            <p className="mt-1 text-sm text-gray-600">
              Configure server-level settings for Stripe billing and email services
            </p>
          </div>

          {/* Message Display */}
          {message && (
            <div className={`mx-6 mt-4 p-4 rounded-md ${
              message.type === 'success' 
                ? 'bg-green-50 text-green-800 border border-green-200' 
                : 'bg-red-50 text-red-800 border border-red-200'
            }`}>
              {message.text}
            </div>
          )}

          {/* Tab Navigation */}
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8 px-6">
              {[
                { id: 'stripe', label: 'Stripe Settings' },
                { id: 'email', label: 'Email Settings' },
                { id: 'twilio', label: 'SMS (Twilio)' },
                { id: 'google-places', label: 'Google Places' },
                { id: 'pricing', label: 'Pricing & Limits' },
                { id: 'billing', label: 'Billing Settings' },
                { id: 'public', label: 'Public Settings' }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {/* Stripe Settings Tab */}
            {activeTab === 'stripe' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Stripe Configuration</h3>
                  <div className="grid grid-cols-1 gap-6">
                    <div>
                      <label htmlFor="stripe.secretKey" className="block text-sm font-medium text-gray-700">
                        Secret Key
                      </label>
                      <input
                        type="password"
                        id="stripe.secretKey"
                        name="stripe.secretKey"
                        value={stripeSettings.secretKey}
                        onChange={handleInputChange}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        placeholder="sk_test_..."
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="stripe.publishableKey" className="block text-sm font-medium text-gray-700">
                        Publishable Key
                      </label>
                      <input
                        type="text"
                        id="stripe.publishableKey"
                        name="stripe.publishableKey"
                        value={stripeSettings.publishableKey}
                        onChange={handleInputChange}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        placeholder="pk_test_..."
                      />
                    </div>
                  </div>
                  
                  <div className="mt-6">
                    <button
                      onClick={saveStripeSettings}
                      disabled={isSaving}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSaving ? 'Saving...' : 'Save Stripe Settings'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Email Settings Tab */}
            {activeTab === 'email' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Email Configuration</h3>
                  <div className="grid grid-cols-1 gap-6">
                    <div>
                      <label htmlFor="email.postmarkToken" className="block text-sm font-medium text-gray-700">
                        Postmark Token
                      </label>
                      <input
                        type="password"
                        id="email.postmarkToken"
                        name="email.postmarkToken"
                        value={emailSettings.postmarkToken}
                        onChange={handleInputChange}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        placeholder="Postmark API token"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="email.fromEmail" className="block text-sm font-medium text-gray-700">
                        From Email
                      </label>
                      <input
                        type="email"
                        id="email.fromEmail"
                        name="email.fromEmail"
                        value={emailSettings.fromEmail}
                        onChange={handleInputChange}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        placeholder="noreply@repman.com"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="email.fromName" className="block text-sm font-medium text-gray-700">
                        From Name
                      </label>
                      <input
                        type="text"
                        id="email.fromName"
                        name="email.fromName"
                        value={emailSettings.fromName}
                        onChange={handleInputChange}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        placeholder="ReputationManager"
                      />
                    </div>
                  </div>
                  
                  <div className="mt-6">
                    <button
                      onClick={saveEmailSettings}
                      disabled={isSaving}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSaving ? 'Saving...' : 'Save Email Settings'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Twilio Settings Tab */}
            {activeTab === 'twilio' && (
              <div className="space-y-6">
                {!isSmsEnabled && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 mb-6">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-yellow-800">SMS Functionality Disabled</h3>
                        <div className="mt-2 text-sm text-yellow-700">
                          <p>SMS functionality is currently disabled. Settings can be configured but won't be active until SMS is enabled via the SMS_ENABLED environment variable.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Twilio SMS Configuration</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Configure your Twilio account to enable SMS campaigns for all tenants.
                    Your credentials are shared across all tenants and stored encrypted.
                  </p>
                  
                  <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-6">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-blue-800">Getting Started</h3>
                        <div className="mt-2 text-sm text-blue-700">
                          <ol className="list-decimal list-inside space-y-1">
                            <li>Go to <a href="https://www.twilio.com/console" target="_blank" rel="noopener noreferrer" className="underline">Twilio Console</a></li>
                            <li>Copy your Account SID and Auth Token</li>
                            <li>Get or buy a phone number</li>
                            <li>Complete A2P 10DLC registration (US only)</li>
                            <li>Enter credentials below</li>
                          </ol>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 gap-6">
                    <div>
                      <label htmlFor="twilio.accountSid" className="block text-sm font-medium text-gray-700">
                        Account SID
                      </label>
                      <input
                        type="text"
                        id="twilio.accountSid"
                        name="twilio.accountSid"
                        value={twilioSettings.accountSid}
                        onChange={handleInputChange}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="twilio.authToken" className="block text-sm font-medium text-gray-700">
                        Auth Token
                      </label>
                      <input
                        type="password"
                        id="twilio.authToken"
                        name="twilio.authToken"
                        value={twilioSettings.authToken}
                        onChange={handleInputChange}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        placeholder="Your Twilio auth token"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Your auth token is stored encrypted for security.
                      </p>
                    </div>
                    
                    <div>
                      <label htmlFor="twilio.phoneNumber" className="block text-sm font-medium text-gray-700">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        id="twilio.phoneNumber"
                        name="twilio.phoneNumber"
                        value={twilioSettings.phoneNumber}
                        onChange={handleInputChange}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        placeholder="+1234567890"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Include country code (e.g., +1 for US). Must be registered with Twilio.
                      </p>
                    </div>
                  </div>
                  
                  <div className="mt-6">
                    <button
                      onClick={saveTwilioSettings}
                      disabled={isSaving}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSaving ? 'Saving...' : 'Save Twilio Settings'}
                    </button>
                  </div>
                  
                  <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-md p-4">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-yellow-800">Important: A2P 10DLC Registration</h3>
                        <div className="mt-2 text-sm text-yellow-700">
                          <p>For US SMS, you must register your brand and campaign with Twilio's A2P 10DLC program. Unregistered numbers may have limited throughput or be blocked.</p>
                          <p className="mt-2">Learn more: <a href="https://www.twilio.com/docs/sms/a2p-10dlc" target="_blank" rel="noopener noreferrer" className="underline">Twilio A2P 10DLC Guide</a></p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Google Places Settings Tab */}
            {activeTab === 'google-places' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Google Places Configuration</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Configure your Google Places API key to enable place search and review analytics for all tenants.
                    This key is shared across all tenants and stored encrypted.
                  </p>
                  
                  <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-6">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-blue-800">Getting Started</h3>
                        <div className="mt-2 text-sm text-blue-700">
                          <ol className="list-decimal list-inside space-y-1">
                            <li>Go to <a href="https://console.cloud.google.com" target="_blank" rel="noopener noreferrer" className="underline">Google Cloud Console</a></li>
                            <li>Enable "Places API (New)"</li>
                            <li>Create an API key</li>
                            <li>Add key restrictions (optional)</li>
                            <li>Paste the key below</li>
                          </ol>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 gap-6">
                    <div>
                      <label htmlFor="google.apiKey" className="block text-sm font-medium text-gray-700">
                        API Key
                      </label>
                      <input
                        type="password"
                        id="google.apiKey"
                        name="google.apiKey"
                        value={googlePlacesSettings.apiKey}
                        onChange={handleInputChange}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        placeholder="AIzaSy..."
                      />
                      <p className="mt-2 text-xs text-gray-500">
                        Your API key is stored encrypted and never exposed to tenants. Free tier: $200/month credit (~28,000 requests).
                      </p>
                    </div>
                  </div>
                  
                  <div className="mt-6">
                    <button
                      onClick={saveGooglePlacesSettings}
                      disabled={isSaving}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSaving ? 'Saving...' : 'Save Google Places Settings'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Pricing Settings Tab */}
            {activeTab === 'pricing' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Pricing & Usage Limits Configuration</h3>
                  <p className="text-sm text-gray-600 mb-6">
                    Configure usage-based pricing and included amounts for each billing plan. These settings determine how much tenants pay for email, SMS, and other services.
                  </p>

                  {/* Plan Pricing */}
                  <div className="mb-8">
                    <h4 className="text-md font-medium text-gray-900 mb-1">Plan Pricing (Monthly)</h4>
                    <p className="text-xs text-gray-500 mb-4">Display-only. Does not change Lemon Squeezy billing.</p>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div>
                          <label htmlFor="plan_price_starter" className="block text-sm font-medium text-gray-700">
                            Starter Plan
                          </label>
                          <div className="mt-1 relative rounded-md shadow-sm">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <span className="text-gray-500 sm:text-sm">$</span>
                            </div>
                            <input
                              type="number"
                              step="0.01"
                              id="plan_price_starter"
                              name="plan_price_starter"
                              value={pricingSettings.plan_price_starter}
                              onChange={handleInputChange}
                              className="block w-full pl-7 pr-12 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                              placeholder="49"
                            />
                          </div>
                        </div>

                        <div>
                          <label htmlFor="plan_price_growth" className="block text-sm font-medium text-gray-700">
                            Growth Plan
                          </label>
                          <div className="mt-1 relative rounded-md shadow-sm">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <span className="text-gray-500 sm:text-sm">$</span>
                            </div>
                            <input
                              type="number"
                              step="0.01"
                              id="plan_price_growth"
                              name="plan_price_growth"
                              value={pricingSettings.plan_price_growth}
                              onChange={handleInputChange}
                              className="block w-full pl-7 pr-12 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                              placeholder="149"
                            />
                          </div>
                        </div>

                        <div>
                          <label htmlFor="plan_price_agency" className="block text-sm font-medium text-gray-700">
                            Agency Plan
                          </label>
                          <div className="mt-1 relative rounded-md shadow-sm">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <span className="text-gray-500 sm:text-sm">$</span>
                            </div>
                            <input
                              type="number"
                              step="0.01"
                              id="plan_price_agency"
                              name="plan_price_agency"
                              value={pricingSettings.plan_price_agency}
                              onChange={handleInputChange}
                              className="block w-full pl-7 pr-12 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                              placeholder="399"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Per-Unit Pricing */}
                  <div className="mb-8">
                    <h4 className="text-md font-medium text-gray-900 mb-4">Per-Unit Pricing (USD)</h4>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label htmlFor="pricing_email_per_send" className="block text-sm font-medium text-gray-700">
                            Email - Price per Send
                          </label>
                          <div className="mt-1 relative rounded-md shadow-sm">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <span className="text-gray-500 sm:text-sm">$</span>
                            </div>
                            <input
                              type="number"
                              step="0.001"
                              id="pricing_email_per_send"
                              name="pricing_email_per_send"
                              value={pricingSettings.pricing_email_per_send}
                              onChange={handleInputChange}
                              className="block w-full pl-7 pr-12 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                              placeholder="0.002"
                            />
                          </div>
                          <p className="mt-1 text-xs text-gray-500">Typical: $0.001-0.003 (Postmark costs ~$0.00125)</p>
                        </div>

                        <div>
                          <label htmlFor="pricing_sms_per_send" className="block text-sm font-medium text-gray-700">
                            SMS - Price per Send
                          </label>
                          <div className="mt-1 relative rounded-md shadow-sm">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <span className="text-gray-500 sm:text-sm">$</span>
                            </div>
                            <input
                              type="number"
                              step="0.001"
                              id="pricing_sms_per_send"
                              name="pricing_sms_per_send"
                              value={pricingSettings.pricing_sms_per_send}
                              onChange={handleInputChange}
                              className="block w-full pl-7 pr-12 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                              placeholder="0.015"
                            />
                          </div>
                          <p className="mt-1 text-xs text-gray-500">Typical: $0.010-0.020 (Twilio costs ~$0.0079)</p>
                        </div>

                        <div>
                          <label htmlFor="pricing_contact_per_month" className="block text-sm font-medium text-gray-700">
                            Contact Storage - Price per Month
                          </label>
                          <div className="mt-1 relative rounded-md shadow-sm">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <span className="text-gray-500 sm:text-sm">$</span>
                            </div>
                            <input
                              type="number"
                              step="0.001"
                              id="pricing_contact_per_month"
                              name="pricing_contact_per_month"
                              value={pricingSettings.pricing_contact_per_month}
                              onChange={handleInputChange}
                              className="block w-full pl-7 pr-12 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                              placeholder="0.01"
                            />
                          </div>
                          <p className="mt-1 text-xs text-gray-500">Typical: $0.005-0.02 per contact</p>
                        </div>

                        <div>
                          <label htmlFor="pricing_feedback_per_item" className="block text-sm font-medium text-gray-700">
                            Feedback Item - Price per Item
                          </label>
                          <div className="mt-1 relative rounded-md shadow-sm">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <span className="text-gray-500 sm:text-sm">$</span>
                            </div>
                            <input
                              type="number"
                              step="0.01"
                              id="pricing_feedback_per_item"
                              name="pricing_feedback_per_item"
                              value={pricingSettings.pricing_feedback_per_item}
                              onChange={handleInputChange}
                              className="block w-full pl-7 pr-12 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                              placeholder="0.10"
                            />
                          </div>
                          <p className="mt-1 text-xs text-gray-500">Typical: $0.05-0.20 per feedback</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Included Amounts - Starter Plan */}
                  <div className="mb-8">
                    <h4 className="text-md font-medium text-gray-900 mb-4">Starter Plan - Included Amounts</h4>
                    <div className="bg-blue-50 rounded-lg p-4">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div>
                          <label htmlFor="included_emails_starter" className="block text-sm font-medium text-gray-700">
                            Included Emails
                          </label>
                          <input
                            type="number"
                            id="included_emails_starter"
                            name="included_emails_starter"
                            value={pricingSettings.included_emails_starter}
                            onChange={handleInputChange}
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          />
                        </div>
                        <div>
                          <label htmlFor="included_sms_starter" className="block text-sm font-medium text-gray-700">
                            Included SMS
                          </label>
                          <input
                            type="number"
                            id="included_sms_starter"
                            name="included_sms_starter"
                            value={pricingSettings.included_sms_starter}
                            onChange={handleInputChange}
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          />
                        </div>
                        <div>
                          <label htmlFor="included_contacts_starter" className="block text-sm font-medium text-gray-700">
                            Included Contacts
                          </label>
                          <input
                            type="number"
                            id="included_contacts_starter"
                            name="included_contacts_starter"
                            value={pricingSettings.included_contacts_starter}
                            onChange={handleInputChange}
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Included Amounts - Growth Plan */}
                  <div className="mb-8">
                    <h4 className="text-md font-medium text-gray-900 mb-4">Growth Plan - Included Amounts</h4>
                    <div className="bg-green-50 rounded-lg p-4">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div>
                          <label htmlFor="included_emails_growth" className="block text-sm font-medium text-gray-700">
                            Included Emails
                          </label>
                          <input
                            type="number"
                            id="included_emails_growth"
                            name="included_emails_growth"
                            value={pricingSettings.included_emails_growth}
                            onChange={handleInputChange}
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          />
                        </div>
                        <div>
                          <label htmlFor="included_sms_growth" className="block text-sm font-medium text-gray-700">
                            Included SMS
                          </label>
                          <input
                            type="number"
                            id="included_sms_growth"
                            name="included_sms_growth"
                            value={pricingSettings.included_sms_growth}
                            onChange={handleInputChange}
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          />
                        </div>
                        <div>
                          <label htmlFor="included_contacts_growth" className="block text-sm font-medium text-gray-700">
                            Included Contacts
                          </label>
                          <input
                            type="number"
                            id="included_contacts_growth"
                            name="included_contacts_growth"
                            value={pricingSettings.included_contacts_growth}
                            onChange={handleInputChange}
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Included Amounts - Agency Plan */}
                  <div className="mb-8">
                    <h4 className="text-md font-medium text-gray-900 mb-4">Agency Plan - Included Amounts</h4>
                    <div className="bg-purple-50 rounded-lg p-4">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div>
                          <label htmlFor="included_emails_agency" className="block text-sm font-medium text-gray-700">
                            Included Emails
                          </label>
                          <input
                            type="number"
                            id="included_emails_agency"
                            name="included_emails_agency"
                            value={pricingSettings.included_emails_agency}
                            onChange={handleInputChange}
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          />
                        </div>
                        <div>
                          <label htmlFor="included_sms_agency" className="block text-sm font-medium text-gray-700">
                            Included SMS
                          </label>
                          <input
                            type="number"
                            id="included_sms_agency"
                            name="included_sms_agency"
                            value={pricingSettings.included_sms_agency}
                            onChange={handleInputChange}
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          />
                        </div>
                        <div>
                          <label htmlFor="included_contacts_agency" className="block text-sm font-medium text-gray-700">
                            Included Contacts
                          </label>
                          <input
                            type="number"
                            id="included_contacts_agency"
                            name="included_contacts_agency"
                            value={pricingSettings.included_contacts_agency}
                            onChange={handleInputChange}
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Pricing Example Calculator */}
                  <div className="bg-green-50 border border-green-200 rounded-md p-4 mb-6">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-green-800">Example Pricing Calculation</h3>
                        <div className="mt-2 text-sm text-green-700">
                          <p><strong>Growth Plan tenant sends:</strong></p>
                          <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
                            <li>10,000 emails (5,000 included + 5,000 overage @ ${pricingSettings.pricing_email_per_send}) = ${(5000 * parseFloat(pricingSettings.pricing_email_per_send || '0')).toFixed(2)} overage</li>
                            <li>800 SMS (500 included + 300 overage @ ${pricingSettings.pricing_sms_per_send}) = ${(300 * parseFloat(pricingSettings.pricing_sms_per_send || '0')).toFixed(2)} overage</li>
                            <li><strong>Total Overage:</strong> ${((5000 * parseFloat(pricingSettings.pricing_email_per_send || '0')) + (300 * parseFloat(pricingSettings.pricing_sms_per_send || '0'))).toFixed(2)}</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6">
                    <button
                      onClick={savePricingSettings}
                      disabled={isSaving}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSaving ? 'Saving...' : 'Save Pricing Settings'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Billing Settings Tab */}
            {activeTab === 'billing' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Billing & Trial Settings</h3>
                  <div className="space-y-6">
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="flex-1">
                        <h4 className="text-sm font-medium text-gray-900">Require Payment Method for Trial</h4>
                        <p className="mt-1 text-sm text-gray-600">
                          When enabled, users must provide a payment method at signup to start their free trial. 
                          This enables automatic billing after the trial period ends.
                        </p>
                        <p className="mt-2 text-xs text-gray-500">
                          When disabled, users can start a trial without a payment method, but must add payment later to continue after trial ends.
                        </p>
                      </div>
                      <div className="ml-4">
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={cardRequiredForTrial}
                            onChange={(e) => setCardRequiredForTrial(e.target.checked)}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                        </label>
                      </div>
                    </div>
                    
                    <div className="mt-6">
                      <button
                        onClick={saveCardRequiredForTrial}
                        disabled={isSaving}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isSaving ? 'Saving...' : 'Save Billing Settings'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Public Settings Tab */}
            {activeTab === 'public' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Public Settings</h3>
                  <p className="text-sm text-gray-600 mb-6">
                    Public settings can be read by all users and are typically used for configuration
                    that doesn't contain sensitive information.
                  </p>
                  
                  {/* Add New Public Setting */}
                  <div className="bg-gray-50 p-4 rounded-lg mb-6">
                    <h4 className="text-md font-medium text-gray-900 mb-3">Add New Setting</h4>
                    <div className="grid grid-cols-1 gap-4">
                      <div>
                        <label htmlFor="public.key" className="block text-sm font-medium text-gray-700">
                          Key
                        </label>
                        <input
                          type="text"
                          id="public.key"
                          name="public.key"
                          value={newPublicSetting.key}
                          onChange={handleInputChange}
                          className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          placeholder="setting_key"
                        />
                      </div>
                      
                      <div>
                        <label htmlFor="public.value" className="block text-sm font-medium text-gray-700">
                          Value
                        </label>
                        <input
                          type="text"
                          id="public.value"
                          name="public.value"
                          value={newPublicSetting.value}
                          onChange={handleInputChange}
                          className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          placeholder="setting_value"
                        />
                      </div>
                      
                      <div>
                        <label htmlFor="public.description" className="block text-sm font-medium text-gray-700">
                          Description (Optional)
                        </label>
                        <textarea
                          id="public.description"
                          name="public.description"
                          value={newPublicSetting.description}
                          onChange={handleInputChange}
                          rows={2}
                          className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          placeholder="Description of this setting"
                        />
                      </div>
                      
                      <div>
                        <button
                          onClick={addPublicSetting}
                          disabled={isSaving}
                          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isSaving ? 'Adding...' : 'Add Setting'}
                        </button>
                      </div>
                    </div>
                  </div>
                  
                  {/* Existing Public Settings */}
                  <div>
                    <h4 className="text-md font-medium text-gray-900 mb-3">Existing Settings</h4>
                    {publicSettings.length === 0 ? (
                      <p className="text-sm text-gray-500">No public settings configured</p>
                    ) : (
                      <div className="space-y-3">
                        {publicSettings.map((setting, index) => (
                          <div key={index} className="bg-gray-50 p-3 rounded-lg">
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <div className="font-medium text-gray-900">{setting.key}</div>
                                <div className="text-sm text-gray-600 mt-1">{setting.value}</div>
                                {setting.description && (
                                  <div className="text-xs text-gray-500 mt-1">{setting.description}</div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
